/**
 * Custom hooks for Branch management
 *
 * This file contains all the custom hooks for branch operations including
 * CRUD operations, form management, and business logic.
 * Following the implementation plan for independent branch system.
 */

import { useToast } from '@/hooks/use-toast';
import { useFormsTranslation } from '@/hooks/useTranslation';
import { extractErrorMessage } from '@/lib/errorUtils';
import {
  createBranchFormSchema,
  type BranchFormSchema,
} from '@/lib/validations/branchValidation';
import { apiService } from '@/services/api';
import type {
  Branch,
  BranchFormData,
  BranchListResponse,
  BranchResponse,
  CreateBranchData,
  DaySchedule,
  UpdateBranchData,
} from '@/types/branch.types';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';

// Error interface for API responses
interface ApiError extends Error {
  response?: {
    data?: {
      message?: string;
    };
  };
}

/**
 * Hook for fetching a single branch by ID
 */
export const useBranch = (branchId: string | null) => {
  return useQuery({
    queryKey: ['branch', branchId],
    queryFn: () => apiService.get<BranchResponse>(`/branches/${branchId}`),
    enabled: !!branchId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook for fetching all branches with pagination
 */
export const useBranches = (
  page = 1,
  perPage = 15,
  filters?: Record<string, string | number>
) => {
  const queryParams = new URLSearchParams({
    page: page.toString(),
    per_page: perPage.toString(),
    ...filters,
  });

  return useQuery({
    queryKey: ['branches', page, perPage, filters],
    queryFn: () =>
      apiService.get<BranchListResponse>(`/branches?${queryParams}`),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook for creating a new branch
 */
export const useCreateBranch = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (branchData: CreateBranchData) =>
      apiService.post<BranchResponse>('/branches', branchData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['branches'] });
      toast({
        title: 'Erfolg',
        description: 'Filiale wurde erfolgreich erstellt.',
      });
    },
    onError: (error: ApiError) => {
      const errorMessage = extractErrorMessage(
        error,
        'Fehler beim Erstellen der Filiale.'
      );
      toast({
        title: 'Fehler',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });
};

/**
 * Hook for updating an existing branch
 */
export const useUpdateBranch = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateBranchData }) =>
      apiService.put<BranchResponse>(`/branches/${id}`, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['branches'] });
      queryClient.invalidateQueries({ queryKey: ['branch', id] });
      toast({
        title: 'Erfolg',
        description: 'Filiale wurde erfolgreich aktualisiert.',
      });
    },
    onError: (error: ApiError) => {
      const errorMessage = extractErrorMessage(
        error,
        'Fehler beim Aktualisieren der Filiale.'
      );
      toast({
        title: 'Fehler',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });
};

/**
 * Hook for deleting a branch
 */
export const useDeleteBranch = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (branchId: string) =>
      apiService.delete(`/branches/${branchId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['branches'] });
      toast({
        title: 'Erfolg',
        description: 'Filiale wurde erfolgreich gelöscht.',
      });
    },
    onError: (error: ApiError) => {
      const errorMessage = extractErrorMessage(
        error,
        'Fehler beim Löschen der Filiale.'
      );
      toast({
        title: 'Fehler',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });
};

/**
 * Hook for managing branch form state with validation
 * Following the ContactPartnerForm pattern with translations
 */
interface UseBranchFormProps {
  branch?: Branch;
  onSave: (data: BranchFormData) => void;
  isOpen: boolean;
}

export const useBranchForm = ({
  branch,
  onSave,
  isOpen,
}: UseBranchFormProps) => {
  const { t: tForms } = useFormsTranslation();

  // Create schema with translations
  const branchFormSchema = createBranchFormSchema(tForms);

  const form = useForm<BranchFormSchema>({
    resolver: zodResolver(branchFormSchema),
    defaultValues: {
      language: 'de',
      name: '',
      country: 'CH',
      street: '',
      number: '',
      postalCode: '',
      zipCode: '',
      city: '',
      canton: '',
      openingHours: {
        monday: {
          enabled: false,
          workingStart: '',
          workingEnd: '',
          breakStart: '',
          breakEnd: '',
        },
        tuesday: {
          enabled: false,
          workingStart: '',
          workingEnd: '',
          breakStart: '',
          breakEnd: '',
        },
        wednesday: {
          enabled: false,
          workingStart: '',
          workingEnd: '',
          breakStart: '',
          breakEnd: '',
        },
        thursday: {
          enabled: false,
          workingStart: '',
          workingEnd: '',
          breakStart: '',
          breakEnd: '',
        },
        friday: {
          enabled: false,
          workingStart: '',
          workingEnd: '',
          breakStart: '',
          breakEnd: '',
        },
        saturday: {
          enabled: false,
          workingStart: '',
          workingEnd: '',
          breakStart: '',
          breakEnd: '',
        },
        sunday: {
          enabled: false,
          workingStart: '',
          workingEnd: '',
          breakStart: '',
          breakEnd: '',
        },
      },
    },
  });

  // Reset form when branch data changes or dialog opens
  useEffect(() => {
    if (isOpen) {
      if (branch) {
        // Editing existing branch
        form.reset({
          language: branch.language || 'de',
          name: branch.name || '',
          country: branch.country || 'CH',
          street: branch.street || '',
          number: branch.number || '',
          postalCode: branch.postalCode || '',
          zipCode: branch.zipCode || '',
          city: branch.city || '',
          canton: branch.canton || '',
          openingHours: branch.openingHours || {
            monday: {
              enabled: false,
              workingStart: '',
              workingEnd: '',
              breakStart: '',
              breakEnd: '',
            },
            tuesday: {
              enabled: false,
              workingStart: '',
              workingEnd: '',
              breakStart: '',
              breakEnd: '',
            },
            wednesday: {
              enabled: false,
              workingStart: '',
              workingEnd: '',
              breakStart: '',
              breakEnd: '',
            },
            thursday: {
              enabled: false,
              workingStart: '',
              workingEnd: '',
              breakStart: '',
              breakEnd: '',
            },
            friday: {
              enabled: false,
              workingStart: '',
              workingEnd: '',
              breakStart: '',
              breakEnd: '',
            },
            saturday: {
              enabled: false,
              workingStart: '',
              workingEnd: '',
              breakStart: '',
              breakEnd: '',
            },
            sunday: {
              enabled: false,
              workingStart: '',
              workingEnd: '',
              breakStart: '',
              breakEnd: '',
            },
          },
        });
      } else {
        // Creating new branch
        form.reset({
          language: 'de',
          name: '',
          country: 'CH',
          street: '',
          number: '',
          postalCode: '',
          zipCode: '',
          city: '',
          canton: '',
          openingHours: {
            monday: {
              enabled: false,
              workingStart: '',
              workingEnd: '',
              breakStart: '',
              breakEnd: '',
            },
            tuesday: {
              enabled: false,
              workingStart: '',
              workingEnd: '',
              breakStart: '',
              breakEnd: '',
            },
            wednesday: {
              enabled: false,
              workingStart: '',
              workingEnd: '',
              breakStart: '',
              breakEnd: '',
            },
            thursday: {
              enabled: false,
              workingStart: '',
              workingEnd: '',
              breakStart: '',
              breakEnd: '',
            },
            friday: {
              enabled: false,
              workingStart: '',
              workingEnd: '',
              breakStart: '',
              breakEnd: '',
            },
            saturday: {
              enabled: false,
              workingStart: '',
              workingEnd: '',
              breakStart: '',
              breakEnd: '',
            },
            sunday: {
              enabled: false,
              workingStart: '',
              workingEnd: '',
              breakStart: '',
              breakEnd: '',
            },
          },
        });
      }
    }
  }, [isOpen, branch, form]);

  const handleSubmit = useCallback(
    (data: BranchFormSchema) => {
      // Convert BranchFormSchema to BranchFormData for compatibility
      onSave(data as BranchFormData);
    },
    [onSave]
  );

  const isEditing = !!branch;

  return {
    form,
    handleSubmit,
    isEditing,
  };
};

/**
 * Hook for managing opening hours logic
 */
export const useOpeningHours = () => {
  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  const applyToAllDays = useCallback(
    (daySchedule: DaySchedule, form: UseFormReturn<BranchFormData>) => {
      const days = [
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
        'saturday',
        'sunday',
      ];
      days.forEach(day => {
        const fieldPath = `openingHours.${day}` as const;
        // @ts-expect-error Dynamic field access for opening hours
        form.setValue(fieldPath, daySchedule);
      });
    },
    []
  );

  const applyToWeekdays = useCallback(
    (daySchedule: DaySchedule, form: UseFormReturn<BranchFormData>) => {
      const weekdays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
      weekdays.forEach(day => {
        const fieldPath = `openingHours.${day}` as const;
        // @ts-expect-error Dynamic field access for opening hours
        form.setValue(fieldPath, daySchedule);
      });
    },
    []
  );

  const applyBusinessHoursPreset = useCallback(
    (preset: string, form: UseFormReturn<BranchFormData>) => {
      const presets = {
        standard: {
          enabled: true,
          workingStart: '08:00',
          workingEnd: '17:00',
          breakStart: '12:00',
          breakEnd: '13:00',
        },
        extended: {
          enabled: true,
          workingStart: '07:00',
          workingEnd: '19:00',
          breakStart: '12:00',
          breakEnd: '13:00',
        },
        banking: {
          enabled: true,
          workingStart: '09:00',
          workingEnd: '16:00',
          breakStart: '12:00',
          breakEnd: '13:00',
        },
        retail: {
          enabled: true,
          workingStart: '10:00',
          workingEnd: '20:00',
          breakStart: '14:00',
          breakEnd: '15:00',
        },
      };

      const schedule = presets[preset as keyof typeof presets];
      if (schedule) {
        applyToWeekdays(schedule, form);
      }
    },
    [applyToWeekdays]
  );

  return {
    selectedDay,
    setSelectedDay,
    applyToAllDays,
    applyToWeekdays,
    applyBusinessHoursPreset,
  };
};

/**
 * Hook for branch search and filtering
 */
export const useBranchSearch = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<Record<string, string | number>>({});

  const updateFilter = useCallback((key: string, value: string | number) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({});
    setSearchTerm('');
  }, []);

  return {
    searchTerm,
    setSearchTerm,
    filters,
    updateFilter,
    clearFilters,
  };
};
